import type { GridColumn } from '../types';

// Backlog/Deferred are off the linear pipeline (parked/rejected projects).
// They're excluded from the main gradient (a dark shade at the far right
// read as "further along than Closed", which is misleading) — cards in
// these stages show a plain text label instead of any bar or cursor.
const BACKLOG_LABELS = new Set(['backlog']);
const DEFERRED_LABELS = new Set(['deferred', 'différé', 'differe', 'reporté', 'reporte']);

// Fixed intensity ladder: each pipeline stage is the accent color mixed with
// white at an increasing ratio, from 25% (earliest stage) to 100% (final
// stage), spread evenly across however many main stages exist. This ratio is
// what stays constant when the accent hue is changed in Admin →
// Personalisation — only the anchor color changes, never the progression.
const RAMP_START = 0.25;
const RAMP_END = 1.0;

function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  const int = m ? parseInt(m[1], 16) : 0;
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
  const c = (n: number) => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

function mixWithWhite(hex: string, t: number): string {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(255 + (r - 255) * t, 255 + (g - 255) * t, 255 + (b - 255) * t);
}

export interface StageColorInfo {
  colors: Map<number, string>; // status_id -> color, main stages only
  ordered: GridColumn[];       // main lifecycle stages only (Inbox…Closed), sorted
  backlog?: GridColumn;
  deferred?: GridColumn;
}

/**
 * Assigns each MAIN lifecycle stage (Inbox…Closed) a color on the accent
 * ramp. Backlog/Deferred are identified but excluded from the ramp — they
 * get their own fixed-color bar via getSecondaryStage() instead. Falls back
 * gracefully — any stage not matching a known label still gets a ramp
 * position by its rank, so custom/renamed pipelines don't break.
 */
export function buildStageColors(pipelineColumns: GridColumn[], accent: string): StageColorInfo {
  const sorted = [...pipelineColumns].sort((a, b) => a.sort_order - b.sort_order);
  const backlog = sorted.find((c) => BACKLOG_LABELS.has((c.label || '').trim().toLowerCase()));
  const deferred = sorted.find((c) => DEFERRED_LABELS.has((c.label || '').trim().toLowerCase()));
  const ordered = sorted.filter((c) => c !== backlog && c !== deferred);

  const colors = new Map<number, string>();
  ordered.forEach((col, i) => {
    const t = ordered.length > 1
      ? RAMP_START + (RAMP_END - RAMP_START) * (i / (ordered.length - 1))
      : RAMP_END;
    colors.set(col.id, mixWithWhite(accent, t));
  });

  return { colors, ordered, backlog, deferred };
}

/** Builds the main-pipeline gradient (Inbox → Closed only), shared by every card. */
export function buildStageGradient(info: StageColorInfo): string | undefined {
  const { ordered, colors } = info;
  if (ordered.length === 0) return undefined;
  if (ordered.length === 1) return colors.get(ordered[0].id);
  const stops = ordered.map((col, i) => `${colors.get(col.id)} ${(i / (ordered.length - 1)) * 100}%`);
  return `linear-gradient(to right, ${stops.join(', ')})`;
}

/** Position (0-100) of the current-stage cursor along the main gradient, or null if the project is in Backlog/Deferred (or an unknown stage). */
export function getStageCursorPercent(statusId: number, info: StageColorInfo): number | null {
  const { ordered } = info;
  if (ordered.length === 0) return null;
  if (ordered.length === 1) return ordered[0].id === statusId ? 0 : null;
  const idx = ordered.findIndex((c) => c.id === statusId);
  if (idx === -1) return null;
  return (idx / (ordered.length - 1)) * 100;
}

export type SecondaryStage = 'backlog' | 'deferred' | null;

/** Whether the project is parked in Backlog/Deferred, i.e. off the main pipeline. */
export function getSecondaryStage(statusId: number, info: StageColorInfo): SecondaryStage {
  if (info.backlog && statusId === info.backlog.id) return 'backlog';
  if (info.deferred && statusId === info.deferred.id) return 'deferred';
  return null;
}
